import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { setNavigator } from "@/lib/navigation";
import { AppLayout } from "@/app/layout/AppLayout";
import { AuthLayout } from "@/app/layout/AuthLayout";
import { PrintLayout } from "@/app/layout/PrintLayout";
import { Skeleton } from "@/components/ui/skeleton";

const LoginPage = lazy(() =>
  import("@/features/auth/pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("@/features/auth/pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const ActivateAccountPage = lazy(() =>
  import("@/features/auth/pages/ActivateAccountPage").then((m) => ({
    default: m.ActivateAccountPage,
  })),
);
const VerifyEmailPage = lazy(() =>
  import("@/features/auth/pages/VerifyEmailPage").then((m) => ({
    default: m.VerifyEmailPage,
  })),
);
const OAuthCallbackPage = lazy(() =>
  import("@/features/auth/pages/OAuthCallbackPage").then((m) => ({
    default: m.OAuthCallbackPage,
  })),
);
const RegisterPage = lazy(() =>
  import("@/features/onboarding/pages/RegisterPage").then((m) => ({ default: m.RegisterPage })),
);
const CompleteProfilePage = lazy(() =>
  import("@/features/onboarding/pages/CompleteProfilePage").then((m) => ({
    default: m.CompleteProfilePage,
  })),
);
const TermsPage = lazy(() =>
  import("@/features/onboarding/pages/TermsPage").then((m) => ({ default: m.TermsPage })),
);
const PlanSelectionPage = lazy(() =>
  import("@/features/onboarding/pages/PlanSelectionPage").then((m) => ({
    default: m.PlanSelectionPage,
  })),
);
const PaymentPage = lazy(() =>
  import("@/features/onboarding/pages/PaymentPage").then((m) => ({ default: m.PaymentPage })),
);
const OnboardingCompletePage = lazy(() =>
  import("@/features/onboarding/pages/OnboardingCompletePage").then((m) => ({
    default: m.OnboardingCompletePage,
  })),
);
const DashboardPage = lazy(() =>
  import("@/features/dashboard/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const LeadsListPage = lazy(() =>
  import("@/features/leads/pages/LeadsListPage").then((m) => ({ default: m.LeadsListPage })),
);
const LeadKanbanPage = lazy(() =>
  import("@/features/leads/pages/LeadKanbanPage").then((m) => ({ default: m.LeadKanbanPage })),
);
const LeadFormPage = lazy(() =>
  import("@/features/leads/pages/LeadFormPage").then((m) => ({ default: m.LeadFormPage })),
);
const LeadDetailPage = lazy(() =>
  import("@/features/leads/pages/LeadDetailPage").then((m) => ({ default: m.LeadDetailPage })),
);
const AgendaPage = lazy(() =>
  import("@/features/agenda/pages/AgendaPage").then((m) => ({ default: m.AgendaPage })),
);

// Placeholders ("Em construção")
const PropertiesPage = lazy(() =>
  import("@/features/properties/pages/PropertiesPage").then((m) => ({ default: m.PropertiesPage })),
);
const PropertyDetailPage = lazy(() =>
  import("@/features/properties/pages/PropertyDetailPage").then((m) => ({
    default: m.PropertyDetailPage,
  })),
);
const ShareAnalyticsPage = lazy(() =>
  import("@/features/properties/pages/ShareAnalyticsPage").then((m) => ({
    default: m.ShareAnalyticsPage,
  })),
);
const PropertySignPage = lazy(() =>
  import("@/features/properties/pages/PropertySignPage").then((m) => ({
    default: m.PropertySignPage,
  })),
);
const PeoplePage = lazy(() =>
  import("@/features/people/pages/PeoplePage").then((m) => ({ default: m.PeoplePage })),
);
const PersonFormPage = lazy(() =>
  import("@/features/people/pages/PersonFormPage").then((m) => ({ default: m.PersonFormPage })),
);
const PersonDetailPage = lazy(() =>
  import("@/features/people/pages/PersonDetailPage").then((m) => ({
    default: m.PersonDetailPage,
  })),
);
const PipelinePage = lazy(() =>
  import("@/features/pipeline/pages/PipelinePage").then((m) => ({ default: m.PipelinePage })),
);
const PipelineSettingsPage = lazy(() =>
  import("@/features/pipeline/pages/PipelineSettingsPage").then((m) => ({
    default: m.PipelineSettingsPage,
  })),
);
const ProposalsPage = lazy(() =>
  import("@/features/proposals/pages/ProposalsPage").then((m) => ({ default: m.ProposalsPage })),
);
const ProposalFormPage = lazy(() =>
  import("@/features/proposals/pages/ProposalFormPage").then((m) => ({
    default: m.ProposalFormPage,
  })),
);
const ProposalDetailPage = lazy(() =>
  import("@/features/proposals/pages/ProposalDetailPage").then((m) => ({
    default: m.ProposalDetailPage,
  })),
);
const LaunchUnitsPage = lazy(() =>
  import("@/features/launches/pages/LaunchUnitsPage").then((m) => ({
    default: m.LaunchUnitsPage,
  })),
);
const LaunchProposalsPage = lazy(() =>
  import("@/features/launches/pages/LaunchProposalsPage").then((m) => ({
    default: m.LaunchProposalsPage,
  })),
);
const LaunchProposalFormPage = lazy(() =>
  import("@/features/launches/pages/LaunchProposalFormPage").then((m) => ({
    default: m.LaunchProposalFormPage,
  })),
);
const LaunchProposalDetailPage = lazy(() =>
  import("@/features/launches/pages/LaunchProposalDetailPage").then((m) => ({
    default: m.LaunchProposalDetailPage,
  })),
);
const LaunchDetailPage = lazy(() =>
  import("@/features/launches/pages/LaunchDetailPage").then((m) => ({
    default: m.LaunchDetailPage,
  })),
);
const LaunchShareAnalyticsPage = lazy(() =>
  import("@/features/launches/pages/LaunchShareAnalyticsPage").then((m) => ({
    default: m.LaunchShareAnalyticsPage,
  })),
);
const DealsPage = lazy(() =>
  import("@/features/deals/pages/DealsPage").then((m) => ({ default: m.DealsPage })),
);
const DealDetailPage = lazy(() =>
  import("@/features/deals/pages/DealDetailPage").then((m) => ({ default: m.DealDetailPage })),
);
const DemandsPage = lazy(() =>
  import("@/features/demands/pages/DemandsPage").then((m) => ({ default: m.DemandsPage })),
);
const DemandFormPage = lazy(() =>
  import("@/features/demands/pages/DemandFormPage").then((m) => ({ default: m.DemandFormPage })),
);
const DemandDetailPage = lazy(() =>
  import("@/features/demands/pages/DemandDetailPage").then((m) => ({ default: m.DemandDetailPage })),
);
const DemandRequestsPage = lazy(() =>
  import("@/features/demands/pages/DemandRequestsPage").then((m) => ({
    default: m.DemandRequestsPage,
  })),
);
const ContractsPage = lazy(() =>
  import("@/features/contracts/pages/ContractsPage").then((m) => ({ default: m.ContractsPage })),
);
const ContractFormPage = lazy(() =>
  import("@/features/contracts/pages/ContractFormPage").then((m) => ({
    default: m.ContractFormPage,
  })),
);
const ContractDetailPage = lazy(() =>
  import("@/features/contracts/pages/ContractDetailPage").then((m) => ({
    default: m.ContractDetailPage,
  })),
);
const InspectionsPage = lazy(() =>
  import("@/features/inspections/pages/InspectionsPage").then((m) => ({
    default: m.InspectionsPage,
  })),
);
const InspectionFormPage = lazy(() =>
  import("@/features/inspections/pages/InspectionFormPage").then((m) => ({
    default: m.InspectionFormPage,
  })),
);
const InspectionDetailPage = lazy(() =>
  import("@/features/inspections/pages/InspectionDetailPage").then((m) => ({
    default: m.InspectionDetailPage,
  })),
);
const InspectionReportPage = lazy(() =>
  import("@/features/inspections/pages/InspectionReportPage").then((m) => ({
    default: m.InspectionReportPage,
  })),
);
const CobrokingPage = lazy(() =>
  import("@/features/cobroking/pages/CobrokingPage").then((m) => ({ default: m.CobrokingPage })),
);
const PartnersPage = lazy(() =>
  import("@/features/partners/pages/PartnersPage").then((m) => ({ default: m.PartnersPage })),
);
const ListingsPage = lazy(() =>
  import("@/features/listings/pages/ListingsPage").then((m) => ({ default: m.ListingsPage })),
);
const CommissionsPage = lazy(() =>
  import("@/features/commissions/pages/CommissionsPage").then((m) => ({
    default: m.CommissionsPage,
  })),
);
const WalletPage = lazy(() =>
  import("@/features/wallet/pages/WalletPage").then((m) => ({ default: m.WalletPage })),
);
const WithdrawalNewPage = lazy(() =>
  import("@/features/wallet/pages/WithdrawalNewPage").then((m) => ({
    default: m.WithdrawalNewPage,
  })),
);
const WithdrawalsListPage = lazy(() =>
  import("@/features/wallet/pages/WithdrawalsListPage").then((m) => ({
    default: m.WithdrawalsListPage,
  })),
);
const WithdrawalDetailPage = lazy(() =>
  import("@/features/wallet/pages/WithdrawalDetailPage").then((m) => ({
    default: m.WithdrawalDetailPage,
  })),
);
const JournalDetailPage = lazy(() =>
  import("@/features/wallet/pages/JournalDetailPage").then((m) => ({
    default: m.JournalDetailPage,
  })),
);
const GoalsPage = lazy(() =>
  import("@/features/goals/pages/GoalsPage").then((m) => ({ default: m.GoalsPage })),
);
const FinancialPage = lazy(() =>
  import("@/features/financial/pages/FinancialPage").then((m) => ({ default: m.FinancialPage })),
);
const FinancialEntryFormPage = lazy(() =>
  import("@/features/financial/pages/FinancialEntryFormPage").then((m) => ({
    default: m.FinancialEntryFormPage,
  })),
);
const FinancialCategoriesPage = lazy(() =>
  import("@/features/financial/pages/FinancialCategoriesPage").then((m) => ({
    default: m.FinancialCategoriesPage,
  })),
);
const AutomationPage = lazy(() =>
  import("@/features/automation/pages/AutomationPage").then((m) => ({ default: m.AutomationPage })),
);
const AutomationFormPage = lazy(() =>
  import("@/features/automation/pages/AutomationFormPage").then((m) => ({
    default: m.AutomationFormPage,
  })),
);
const AutomationRunsPage = lazy(() =>
  import("@/features/automation/pages/AutomationRunsPage").then((m) => ({
    default: m.AutomationRunsPage,
  })),
);
const ReportsPage = lazy(() =>
  import("@/features/reports/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const SettingsLayout = lazy(() =>
  import("@/features/settings/pages/SettingsLayout").then((m) => ({ default: m.SettingsLayout })),
);
const ProfilePage = lazy(() =>
  import("@/features/settings/pages/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const AccountPage = lazy(() =>
  import("@/features/settings/pages/AccountPage").then((m) => ({ default: m.AccountPage })),
);
const IntegrationsPage = lazy(() =>
  import("@/features/settings/pages/IntegrationsPage").then((m) => ({
    default: m.IntegrationsPage,
  })),
);
const TeamPage = lazy(() =>
  import("@/features/settings/pages/TeamPage").then((m) => ({ default: m.TeamPage })),
);
const BrandingPage = lazy(() =>
  import("@/features/settings/pages/BrandingPage").then((m) => ({ default: m.BrandingPage })),
);
const PipelinesRedirect = lazy(() =>
  import("@/features/settings/pages/PipelinesRedirect").then((m) => ({
    default: m.PipelinesRedirect,
  })),
);
const CoveragePage = lazy(() =>
  import("@/features/settings/pages/CoveragePage").then((m) => ({ default: m.CoveragePage })),
);
const NotificationsPrefsPage = lazy(() =>
  import("@/features/settings/pages/NotificationsPrefsPage").then((m) => ({
    default: m.NotificationsPrefsPage,
  })),
);
const PrivacyPage = lazy(() =>
  import("@/features/settings/pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
const BrokerSignPage = lazy(() =>
  import("@/features/settings/pages/BrokerSignPage").then((m) => ({ default: m.BrokerSignPage })),
);
const NotificationsPage = lazy(() =>
  import("@/features/notifications/pages/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const ReferralsPage = lazy(() =>
  import("@/features/referrals/pages/ReferralsPage").then((m) => ({ default: m.ReferralsPage })),
);
const PublicPropertyPage = lazy(() =>
  import("@/features/properties/pages/PublicPropertyPage").then((m) => ({
    default: m.PublicPropertyPage,
  })),
);
const PropertyAccessConfirmPage = lazy(() =>
  import("@/features/properties/pages/PropertyAccessConfirmPage").then((m) => ({
    default: m.PropertyAccessConfirmPage,
  })),
);
const PersonRegistrationPage = lazy(() =>
  import("@/features/people/pages/PersonRegistrationPage").then((m) => ({
    default: m.PersonRegistrationPage,
  })),
);
const SupportTicketsPage = lazy(() =>
  import("@/features/support/pages/SupportTicketsPage").then((m) => ({
    default: m.SupportTicketsPage,
  })),
);
const SupportTicketFormPage = lazy(() =>
  import("@/features/support/pages/SupportTicketFormPage").then((m) => ({
    default: m.SupportTicketFormPage,
  })),
);
const SupportTicketDetailPage = lazy(() =>
  import("@/features/support/pages/SupportTicketDetailPage").then((m) => ({
    default: m.SupportTicketDetailPage,
  })),
);
const ShowcasesPage = lazy(() =>
  import("@/features/showcases/pages/ShowcasesPage").then((m) => ({
    default: m.ShowcasesPage,
  })),
);
const ShowcaseAnalyticsPage = lazy(() =>
  import("@/features/showcases/pages/ShowcaseAnalyticsPage").then((m) => ({
    default: m.ShowcaseAnalyticsPage,
  })),
);

function PageFallback() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-96" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // Rota pública (sem auth) — link compartilhado de imóvel via shareCode opaco
  { path: "/p/:code", element: <Lazy><PublicPropertyPage /></Lazy> },
  // Rota pública (sem auth) — link de autorização de acesso enviado ao proprietário
  { path: "/p/acesso/:token", element: <Lazy><PropertyAccessConfirmPage /></Lazy> },
  // Rota pública (sem auth) — link de auto-cadastro de Pessoa enviado ao cliente
  // (futuro: migra para imobgo-site/app/lead/[token] conforme hub#56)
  { path: "/p/lead/:token", element: <Lazy><PersonRegistrationPage /></Lazy> },
  // Rota pública (sem auth) — retorno do social login (OAuth code + PKCE)
  { path: "/auth/callback/:provider", element: <Lazy><OAuthCallbackPage /></Lazy> },
  // Onboarding self-serve (tela própria, sem chrome do app) — ver hub#209
  // `/register` é a URL PÚBLICA deste passo: é para cá que apontam a landing e o convite
  // do afiliado (`imobgo-public`) e o `Cobroking__JoinUrl` do crm-api. Renomear aqui
  // quebra os três em silêncio — o `path: "*"` lá embaixo manda a rota ausente para o
  // login em vez de dar 404. Ver `public-signup-route.test.ts`.
  { path: "/register", element: <Lazy><RegisterPage /></Lazy> },
  { path: "/onboarding/complete-profile", element: <Lazy><CompleteProfilePage /></Lazy> },
  { path: "/onboarding/terms", element: <Lazy><TermsPage /></Lazy> },
  { path: "/onboarding/plan", element: <Lazy><PlanSelectionPage /></Lazy> },
  { path: "/onboarding/payment", element: <Lazy><PaymentPage /></Lazy> },
  { path: "/onboarding/concluido", element: <Lazy><OnboardingCompletePage /></Lazy> },
  {
    element: <AuthLayout />,
    children: [
      { path: "/login", element: <Lazy><LoginPage /></Lazy> },
      { path: "/forgot-password", element: <Lazy><ForgotPasswordPage /></Lazy> },
      { path: "/reset-password", element: <Lazy><ResetPasswordPage /></Lazy> },
      { path: "/activate-account", element: <Lazy><ActivateAccountPage /></Lazy> },
      { path: "/verify-email", element: <Lazy><VerifyEmailPage /></Lazy> },
    ],
  },
  {
    // Páginas-documento (impressão / download em PDF) — autenticadas, mas sem
    // o chrome do portal (sidebar, topbar, banners, toasts). Abrem em nova aba.
    element: <PrintLayout />,
    children: [
      { path: "/inspections/:id/report", element: <Lazy><InspectionReportPage /></Lazy> },
    ],
  },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "/dashboard", element: <Lazy><DashboardPage /></Lazy> },
      { path: "/properties", element: <Lazy><PropertiesPage /></Lazy> },
      { path: "/properties/:id", element: <Lazy><PropertyDetailPage /></Lazy> },
      { path: "/properties/:id/share", element: <Lazy><ShareAnalyticsPage /></Lazy> },
      { path: "/properties/:id/sign", element: <Lazy><PropertySignPage /></Lazy> },
      { path: "/people", element: <Lazy><PeoplePage /></Lazy> },
      { path: "/people/new", element: <Lazy><PersonFormPage /></Lazy> },
      { path: "/people/:id", element: <Lazy><PersonDetailPage /></Lazy> },
      { path: "/people/:id/edit", element: <Lazy><PersonFormPage /></Lazy> },
      { path: "/leads", element: <Lazy><LeadsListPage /></Lazy> },
      { path: "/leads/kanban", element: <Lazy><LeadKanbanPage /></Lazy> },
      { path: "/leads/new", element: <Lazy><LeadFormPage /></Lazy> },
      { path: "/leads/:id", element: <Lazy><LeadDetailPage /></Lazy> },
      { path: "/leads/:id/edit", element: <Lazy><LeadFormPage /></Lazy> },
      { path: "/pipeline", element: <Lazy><PipelinePage /></Lazy> },
      { path: "/pipeline/settings", element: <Lazy><PipelineSettingsPage /></Lazy> },
      { path: "/proposals", element: <Lazy><ProposalsPage /></Lazy> },
      { path: "/proposals/new", element: <Lazy><ProposalFormPage /></Lazy> },
      { path: "/proposals/:id", element: <Lazy><ProposalDetailPage /></Lazy> },
      { path: "/proposals/:id/edit", element: <Lazy><ProposalFormPage /></Lazy> },
      { path: "/launches", element: <Lazy><LaunchUnitsPage /></Lazy> },
      { path: "/launches/proposals", element: <Lazy><LaunchProposalsPage /></Lazy> },
      { path: "/launches/proposals/new", element: <Lazy><LaunchProposalFormPage /></Lazy> },
      { path: "/launches/proposals/:id", element: <Lazy><LaunchProposalDetailPage /></Lazy> },
      { path: "/launches/proposals/:id/edit", element: <Lazy><LaunchProposalFormPage /></Lazy> },
      { path: "/launches/:id", element: <Lazy><LaunchDetailPage /></Lazy> },
      { path: "/launches/:id/share", element: <Lazy><LaunchShareAnalyticsPage /></Lazy> },
      { path: "/deals", element: <Lazy><DealsPage /></Lazy> },
      { path: "/deals/:id", element: <Lazy><DealDetailPage /></Lazy> },

      { path: "/demands", element: <Lazy><DemandsPage /></Lazy> },
      { path: "/demands/new", element: <Lazy><DemandFormPage /></Lazy> },
      { path: "/demands/:id", element: <Lazy><DemandDetailPage /></Lazy> },
      { path: "/demands/:id/edit", element: <Lazy><DemandFormPage /></Lazy> },
      { path: "/demand-requests", element: <Lazy><DemandRequestsPage /></Lazy> },
      { path: "/agenda", element: <Lazy><AgendaPage /></Lazy> },
      { path: "/contracts", element: <Lazy><ContractsPage /></Lazy> },
      { path: "/contracts/new", element: <Lazy><ContractFormPage /></Lazy> },
      { path: "/contracts/:id", element: <Lazy><ContractDetailPage /></Lazy> },
      { path: "/contracts/:id/edit", element: <Lazy><ContractFormPage /></Lazy> },
      { path: "/inspections", element: <Lazy><InspectionsPage /></Lazy> },
      { path: "/inspections/new", element: <Lazy><InspectionFormPage /></Lazy> },
      { path: "/inspections/:id", element: <Lazy><InspectionDetailPage /></Lazy> },
      { path: "/inspections/:id/edit", element: <Lazy><InspectionFormPage /></Lazy> },
      { path: "/cobroking", element: <Lazy><CobrokingPage /></Lazy> },
      { path: "/parceiros", element: <Lazy><PartnersPage /></Lazy> },
      { path: "/referrals", element: <Lazy><ReferralsPage /></Lazy> },
      { path: "/listings", element: <Lazy><ListingsPage /></Lazy> },
      { path: "/commissions", element: <Lazy><CommissionsPage /></Lazy> },
      { path: "/wallet", element: <Lazy><WalletPage /></Lazy> },
      { path: "/wallet/withdrawals", element: <Lazy><WithdrawalsListPage /></Lazy> },
      { path: "/wallet/withdrawals/new", element: <Lazy><WithdrawalNewPage /></Lazy> },
      { path: "/wallet/withdrawals/:id", element: <Lazy><WithdrawalDetailPage /></Lazy> },
      { path: "/wallet/journals/:id", element: <Lazy><JournalDetailPage /></Lazy> },
      { path: "/goals", element: <Lazy><GoalsPage /></Lazy> },
      { path: "/financial", element: <Lazy><FinancialPage /></Lazy> },
      { path: "/financial/entries/new", element: <Lazy><FinancialEntryFormPage /></Lazy> },
      { path: "/financial/categories", element: <Lazy><FinancialCategoriesPage /></Lazy> },
      { path: "/automation", element: <Lazy><AutomationPage /></Lazy> },
      { path: "/automation/new", element: <Lazy><AutomationFormPage /></Lazy> },
      { path: "/automation/:id", element: <Lazy><AutomationFormPage /></Lazy> },
      { path: "/automation/:id/runs", element: <Lazy><AutomationRunsPage /></Lazy> },
      { path: "/reports", element: <Lazy><ReportsPage /></Lazy> },
      {
        path: "/settings",
        element: <Lazy><SettingsLayout /></Lazy>,
        children: [
          { index: true, element: <Lazy><SettingsPage /></Lazy> },
          { path: "profile", element: <Lazy><ProfilePage /></Lazy> },
          { path: "account", element: <Lazy><AccountPage /></Lazy> },
          { path: "integrations", element: <Lazy><IntegrationsPage /></Lazy> },
          { path: "team", element: <Lazy><TeamPage /></Lazy> },
          { path: "branding", element: <Lazy><BrandingPage /></Lazy> },
          { path: "pipelines", element: <Lazy><PipelinesRedirect /></Lazy> },
          { path: "coverage", element: <Lazy><CoveragePage /></Lazy> },
          { path: "notifications", element: <Lazy><NotificationsPrefsPage /></Lazy> },
          { path: "privacy", element: <Lazy><PrivacyPage /></Lazy> },
          { path: "sign", element: <Lazy><BrokerSignPage /></Lazy> },
        ],
      },
      { path: "/notifications", element: <Lazy><NotificationsPage /></Lazy> },
      { path: "/support", element: <Lazy><SupportTicketsPage /></Lazy> },
      { path: "/support/new", element: <Lazy><SupportTicketFormPage /></Lazy> },
      { path: "/support/:id", element: <Lazy><SupportTicketDetailPage /></Lazy> },
      { path: "/showcases", element: <Lazy><ShowcasesPage /></Lazy> },
      { path: "/showcases/:id/analytics", element: <Lazy><ShowcaseAnalyticsPage /></Lazy> },
    ],
  },
  { path: "*", element: <Navigate to="/dashboard" replace /> },
]);

// Registra o navigate do router na ponte (@/lib/navigation) p/ código fora da árvore
// React navegar via SPA — ex.: o interceptor do axios no gate 402 (sem full reload).
setNavigator((to) => {
  void router.navigate(to);
});
